import React, { useState } from 'react';
import { useAlerts } from '@/context/AlertContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  AlertTriangle, 
  Bell, 
  X, 
  Check, 
  MapPin, 
  Package, 
  AlertCircle,
  Info,
  ExternalLink,
  Eye,
  EyeOff
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface GlobalAlertNotificationProps {
  className?: string;
}

export function GlobalAlertNotification({ className }: GlobalAlertNotificationProps) {
  const { state, resolveAlert, deleteAlert, markAllRead, getUnresolvedAlerts } = useAlerts();
  const [isOpen, setIsOpen] = useState(false);
  const [showResolved, setShowResolved] = useState(false);

  const unresolvedAlerts = getUnresolvedAlerts();
  const criticalAlerts = unresolvedAlerts.filter(alert => alert.severity === 'critical');
  const highAlerts = unresolvedAlerts.filter(alert => alert.severity === 'high');
  const mediumAlerts = unresolvedAlerts.filter(alert => alert.severity === 'medium');
  const lowAlerts = unresolvedAlerts.filter(alert => alert.severity === 'low');

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'missing':
        return <Package className="h-4 w-4" />;
      case 'location_change':
        return <MapPin className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getAlertColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'high':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'medium':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'low':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getBadgeVariant = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'destructive' as const;
      case 'high':
        return 'destructive' as const;
      case 'medium':
        return 'secondary' as const;
      case 'low':
        return 'outline' as const;
      default:
        return 'outline' as const;
    }
  };

  const handleResolveAlert = (alertId: string) => {
    resolveAlert(alertId);
  };

  const handleDeleteAlert = (alertId: string) => {
    deleteAlert(alertId);
  };

  const handleMarkAllRead = () => {
    markAllRead();
  };

  // Show critical alerts prominently
  const showCriticalAlerts = criticalAlerts.length > 0;

  return (
    <div className={className}>
      {/* Critical Alerts - Always Visible */}
      {showCriticalAlerts && (
        <Alert className="border-red-200 bg-red-50 mb-4">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">Critical Alert{criticalAlerts.length > 1 ? 's' : ''}</p>
                <p className="text-sm">
                  {criticalAlerts.length} critical issue{criticalAlerts.length > 1 ? 's' : ''} requiring immediate attention
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsOpen(true)}
                className="text-red-600 border-red-200 hover:bg-red-100"
              >
                View Details
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Alert Notification Button */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={`relative ${showCriticalAlerts ? 'border-red-200 text-red-600 hover:bg-red-50' : ''}`}
          >
            <Bell className="h-4 w-4 mr-2" />
            Alerts
            {state.unreadCount > 0 && (
              <Badge variant="destructive" className="absolute -top-2 -right-2 h-5 w-5 p-0 text-xs">
                {state.unreadCount > 99 ? '99+' : state.unreadCount}
              </Badge>
            )}
          </Button>
        </DialogTrigger>
        
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                System Alerts
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowResolved(!showResolved)}
                >
                  {showResolved ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                  {showResolved ? 'Hide' : 'Show'} Resolved
                </Button>
                {state.unreadCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleMarkAllRead}
                  >
                    Mark All Read
                  </Button>
                )}
              </div>
            </DialogTitle>
            <DialogDescription>
              {unresolvedAlerts.length} unresolved alerts across all sections
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4">
              {/* Alert Summary */}
              {unresolvedAlerts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Check className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p className="font-medium">No Active Alerts</p>
                  <p className="text-sm">All systems are operating normally</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Critical Alerts */}
                  {criticalAlerts.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Critical ({criticalAlerts.length})
                      </h4>
                      <div className="space-y-2">
                        {criticalAlerts.map(alert => (
                          <Alert key={alert.id} className={getAlertColor(alert.severity)}>
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-2">
                                {getAlertIcon(alert.type)}
                                <div className="flex-1">
                                  <p className="font-medium">{alert.message}</p>
                                  <p className="text-xs opacity-75">
                                    Invoice: {alert.invoiceId} | PO: {alert.poId} | {alert.section}
                                  </p>
                                  <p className="text-xs opacity-75">
                                    {formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true })}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Badge variant={getBadgeVariant(alert.severity)} className="text-xs">
                                  {alert.severity}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleResolveAlert(alert.id)}
                                  className="h-6 w-6 p-0"
                                >
                                  <Check className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteAlert(alert.id)}
                                  className="h-6 w-6 p-0 text-red-600 hover:text-red-800"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </Alert>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* High Priority Alerts */}
                  {highAlerts.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        High Priority ({highAlerts.length})
                      </h4>
                      <div className="space-y-2">
                        {highAlerts.map(alert => (
                          <Alert key={alert.id} className={getAlertColor(alert.severity)}>
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-2">
                                {getAlertIcon(alert.type)}
                                <div className="flex-1">
                                  <p className="font-medium">{alert.message}</p>
                                  <p className="text-xs opacity-75">
                                    Invoice: {alert.invoiceId} | PO: {alert.poId} | {alert.section}
                                  </p>
                                  <p className="text-xs opacity-75">
                                    {formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true })}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Badge variant={getBadgeVariant(alert.severity)} className="text-xs">
                                  {alert.severity}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleResolveAlert(alert.id)}
                                  className="h-6 w-6 p-0"
                                >
                                  <Check className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteAlert(alert.id)}
                                  className="h-6 w-6 p-0 text-red-600 hover:text-red-800"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </Alert>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Medium Priority Alerts */}
                  {mediumAlerts.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                        <Info className="h-4 w-4" />
                        Medium Priority ({mediumAlerts.length})
                      </h4>
                      <div className="space-y-2">
                        {mediumAlerts.map(alert => (
                          <Alert key={alert.id} className={getAlertColor(alert.severity)}>
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-2">
                                {getAlertIcon(alert.type)}
                                <div className="flex-1">
                                  <p className="font-medium">{alert.message}</p>
                                  <p className="text-xs opacity-75">
                                    Invoice: {alert.invoiceId} | PO: {alert.poId} | {alert.section}
                                  </p>
                                  <p className="text-xs opacity-75">
                                    {formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true })}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Badge variant={getBadgeVariant(alert.severity)} className="text-xs">
                                  {alert.severity}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleResolveAlert(alert.id)}
                                  className="h-6 w-6 p-0"
                                >
                                  <Check className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteAlert(alert.id)}
                                  className="h-6 w-6 p-0 text-red-600 hover:text-red-800"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </Alert>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Low Priority Alerts */}
                  {lowAlerts.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        <Info className="h-4 w-4" />
                        Low Priority ({lowAlerts.length})
                      </h4>
                      <div className="space-y-2">
                        {lowAlerts.map(alert => (
                          <Alert key={alert.id} className={getAlertColor(alert.severity)}>
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-2">
                                {getAlertIcon(alert.type)}
                                <div className="flex-1">
                                  <p className="font-medium">{alert.message}</p>
                                  <p className="text-xs opacity-75">
                                    Invoice: {alert.invoiceId} | PO: {alert.poId} | {alert.section}
                                  </p>
                                  <p className="text-xs opacity-75">
                                    {formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true })}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Badge variant={getBadgeVariant(alert.severity)} className="text-xs">
                                  {alert.severity}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleResolveAlert(alert.id)}
                                  className="h-6 w-6 p-0"
                                >
                                  <Check className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteAlert(alert.id)}
                                  className="h-6 w-6 p-0 text-red-600 hover:text-red-800"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </Alert>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}




